import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { CheckCircle2, Loader2, XCircle, Clock, MessageSquare, RefreshCw } from 'lucide-react';
import { formatMoney } from '../lib/formatMoney';

const API = process.env.REACT_APP_BACKEND_URL;

// Polling window — Stripe test-mode normally confirms within 3–5s, but on
// slow networks the webhook can take 20s. Poll 18 times every 2s = 36s
// before giving up. Transient backend/network errors DON'T bail early —
// only an explicit `expired` or a hit-the-limit timeout does.
const MAX_ATTEMPTS = 18;
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  // What the server says this checkout was: the booking's portal token, the
  // line's label, the amount, and the balance left. `token` in the URL is the
  // PAYMENT row's token and is deliberately not used for anything.
  const [receipt, setReceipt] = useState(null);
  const [recheckCount, setRecheckCount] = useState(0);
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);
  const cancelledRef = useRef(false);
  const chargedCentsRef = useRef(null);

  const poll = useCallback(async () => {
    if (cancelledRef.current) return;
    let data = null;
    try {
      const res = await fetch(`${API}/api/payments/stripe/status/${sessionId}`);
      if (res.ok) data = await res.json();
    } catch { /* treat as transient — retry */ }

    if (cancelledRef.current) return;

    if (data?.receipt) setReceipt(data.receipt);
    if (typeof data?.amount_total === 'number' && data.amount_total > 0) {
      chargedCentsRef.current = data.amount_total;
    }

    if (data?.payment_status === 'paid') { setStatus('paid'); return; }
    if (data?.status === 'expired') { setStatus('expired'); return; }

    attemptsRef.current += 1;
    if (attemptsRef.current >= MAX_ATTEMPTS) {
      setStatus('timeout');
      return;
    }
    timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    cancelledRef.current = false;
    attemptsRef.current = 0;
    setStatus('checking');
    poll();
    return () => {
      cancelledRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // Re-running the poll when the user clicks "Re-check payment" is what
    // drives recheckCount into the deps — intentional.
  }, [sessionId, recheckCount, poll]);

  const handleRecheck = () => {
    if (!sessionId) return;
    setRecheckCount((c) => c + 1);
  };

  // THE ROOT CAUSE, FIXED. This was `token ? `/client-view/${token}` : null`,
  // built from the `token` query parameter Stripe echoes back — which is the
  // PAYMENT ROW's token. `/client-view/:token` resolves BOOKING portal tokens
  // (`client_sends`), so it found nothing and told a client who had just paid
  // "This link isn't active". The booking's token is now resolved server-side
  // and verified against `client_sends` before it is ever handed back, so this
  // link either resolves or is not rendered.
  const portalToken = receipt?.portalToken || null;
  const portalHref = portalToken ? `/client-view/${portalToken}` : null;

  // A paying client landing on a dead link is a failure, not a cosmetic gap.
  useEffect(() => {
    if (status === 'paid' && receipt && !receipt.portalToken) {
      Sentry.captureMessage('payment success: booking portal token unresolved', {
        level: 'error',
        extra: { sessionId, label: receipt.label || null },
      });
    }
  }, [status, receipt, sessionId]);

  const chargedCents = chargedCentsRef.current;
  const summaryLines = [];
  // The charged figure comes from the stored checkout row (fee gross-up
  // included) and falls back to Stripe's own session total.
  const chargedAmount = receipt?.amountCharged != null
    ? receipt.amountCharged
    : (chargedCents != null ? chargedCents / 100 : null);
  if (chargedAmount != null) {
    summaryLines.push({
      key: 'charged',
      testid: 'payment-success-amount',
      text: `${formatMoney(chargedAmount)} charged to your card`,
    });
  }
  if (receipt?.label) {
    summaryLines.push({
      key: 'label',
      testid: 'payment-success-label',
      text: `For: ${receipt.label}`,
    });
  }
  // UNKNOWN PRINTS NOTHING. `outstandingKnown` false means the ledger could
  // not be read — never render $0.00 or "paid in full" off a failed read.
  if (receipt?.outstandingKnown) {
    summaryLines.push({
      key: 'balance',
      testid: 'payment-success-balance',
      text: receipt.outstanding > 0.005
        ? `Remaining balance on your booking: ${formatMoney(receipt.outstanding)}`
        : 'Paid in full — nothing further is due.',
    });
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#080A14' }}>
      <div
        className="rounded-2xl p-8 text-center max-w-md w-full mx-4"
        style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
        data-testid="payment-success-page"
      >
        {status === 'checking' && (
          <>
            <Loader2 className="w-12 h-12 text-[#D4AF37]/40 animate-spin mx-auto mb-4" />
            <h2 className="text-[18px] font-semibold text-white/70">Processing Payment...</h2>
            <p className="text-[13px] text-white/30 mt-2">Please wait while we confirm your payment.</p>
            <p className="text-[11px] text-white/15 mt-4">This can take up to 30 seconds on slower connections.</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <CheckCircle2 className="w-14 h-14 text-[#22C55E]/60 mx-auto mb-4" />
            <h2 className="text-[20px] font-bold text-[#22C55E]/80">Payment Successful!</h2>
            <p className="text-[13px] text-white/40 mt-2">Thank you! Your payment has been received.</p>
            {summaryLines.length > 0 && (
              <div className="mt-4 space-y-1" data-testid="payment-success-summary">
                {summaryLines.map((line) => (
                  <p key={line.key} className="text-[13px] text-white/60" data-testid={line.testid}>
                    {line.text}
                  </p>
                ))}
              </div>
            )}
            {portalHref ? (
              <a
                href={portalHref}
                className="inline-block mt-6 px-6 py-2.5 rounded-xl text-[12px] font-semibold transition-all hover:-translate-y-px"
                style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.15), rgba(212,175,55,0.06))', border: '1px solid rgba(212,175,55,0.2)', color: '#D4AF37' }}
                data-testid="payment-return-portal-btn"
              >
                Return to Portal
              </a>
            ) : (
              <p className="text-[12px] text-white/35 mt-6" data-testid="payment-portal-unavailable">
                Your payment is recorded. To view your booking, please use the portal
                link in your email.
              </p>
            )}
          </>
        )}
        {(status === 'expired' || status === 'error' || status === 'timeout') && (
          <>
            {status === 'expired' ? (
              <XCircle className="w-14 h-14 text-[#EF4444]/40 mx-auto mb-4" />
            ) : (
              // "Still Confirming…" (timeout/error) is a benign "payment is
              // still being processed, no action needed" state — a red failure
              // ✕ makes a paying client think it failed. Use a calm amber clock.
              <Clock className="w-14 h-14 text-[#D4AF37]/50 mx-auto mb-4" />
            )}
            <h2 className="text-[18px] font-semibold text-white/70">
              {status === 'expired' ? 'Session Expired' : 'Still Confirming…'}
            </h2>
            <p className="text-[13px] text-white/40 mt-2 leading-relaxed">
              {status === 'expired'
                ? 'This payment session has expired. You can try the payment again from your portal.'
                : 'Your payment is still being processed. If you were charged, you\'ll see the payment reflected shortly — no action needed.'}
            </p>
            <div className="flex items-center justify-center gap-2 mt-6 flex-wrap">
              {sessionId && status !== 'expired' && (
                <button
                  type="button"
                  onClick={handleRecheck}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-[12px] font-semibold transition-all hover:-translate-y-px"
                  style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.15), rgba(212,175,55,0.06))', border: '1px solid rgba(212,175,55,0.2)', color: '#D4AF37' }}
                  data-testid="payment-recheck-btn"
                  title="Try confirming your payment again — useful if you just paid and the confirmation was slow."
                >
                  <RefreshCw className="w-3 h-3" />
                  I Paid — Re-check
                </button>
              )}
              {portalHref && (
                <a
                  href={portalHref}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-[12px] font-semibold transition-all hover:-translate-y-px"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)' }}
                  data-testid="payment-return-portal-btn"
                >
                  Return to Portal
                </a>
              )}
              {portalHref && (
                <a
                  href={`${portalHref}#contact`}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-[12px] font-semibold transition-all hover:-translate-y-px"
                  style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.7)' }}
                  data-testid="payment-contact-admin-btn"
                >
                  <MessageSquare className="w-3 h-3" />
                  Contact Admin
                </a>
              )}
            </div>
            {sessionId && (
              <p className="text-[10px] text-white/20 mt-5 tabular-nums select-all">
                Ref: {sessionId.slice(-16)}
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
